import { ref, computed, type Ref } from "vue";
import { cloneDeep } from "lodash";
import { NotationAttributes } from "common/baseTypes";
import useUserOutgoingOperations from "./userOutgoingOperationsHelper";
import useAuthorizationHelper from "./authorizationHelper";
import { useNotationStore } from "../store/pinia/notationStore";

export default function undoRedoHelper(apiHelper: any) {
  const undoStack = ref<Map<String, NotationAttributes>[]>([]);
  const redoStack = ref<Map<String, NotationAttributes>[]>([]);
  const maxStackSize = 20; // Limit stack size to prevent memory issues
  let groupDepth = 0;
  let groupSnapshot: Map<String, NotationAttributes> | null = null;

  const userOutgoingOperations = useUserOutgoingOperations();
  const authorizationHelper = useAuthorizationHelper();
  const notationStore = useNotationStore();

  async function syncToDB(
    currentNotations: Map<String, NotationAttributes>,
    previousNotations: Map<String, NotationAttributes>,
  ) {
    if (
      notationStore.getParent()?.type === "LESSON" &&
      !authorizationHelper.canEdit()
    ) {
      return;
    }

    const added = Array.from(currentNotations.values()).filter(
      (n) => !previousNotations.has(n.uuid),
    );
    const removed = Array.from(previousNotations.values()).filter(
      (n) => !currentNotations.has(n.uuid),
    );
    const updated = Array.from(currentNotations.values()).filter((n) => {
      const prev = previousNotations.get(n.uuid);
      return prev && JSON.stringify(n) !== JSON.stringify(prev); // Simple diff
    });

    // Sync added notations
    for (const n of added) {
      delete n["id"];
      (n as any)["parentUUId"] =
        n.boardType === "LESSON"
          ? (n as any)["lesson"].uuid
          : n.boardType === "QUESTION"
            ? (n as any)["v"].uuid
            : n.boardType === "ANSWER"
              ? (n as any)["answer"].uuid
              : null;

      await apiHelper.addNotation(n); // Use addNotation
      if (n.boardType === "LESSON") {
         await userOutgoingOperations.syncOutgoingAddNotation(n);
      }

      delete (n as any)["lesson"];
      delete (n as any)["question"];
      delete (n as any)["answer"];
    }

    // Sync removed notations
    for (const n of removed) {
      await apiHelper.deleteNotation(n);
      if (n.boardType === "LESSON") {
        await userOutgoingOperations.syncOutgoingRemoveNotation(
          n.uuid,
          (n as any).lesson.uuid,
        );
      }
    }

    // Sync updated notations
    for (const n of updated) {
      await apiHelper.updateNotation(n);
      if (n.boardType === "LESSON") {
        await userOutgoingOperations.syncOutgoingUpdateNotation(n);
      }
    }
  }

  function areMapsEqual(
    a: Map<String, NotationAttributes>,
    b: Map<String, NotationAttributes>,
  ) {
    if (a.size !== b.size) return false;
    for (const [key, value] of a) {
      const other = b.get(key);
      if (!other) return false;
      if (JSON.stringify(value) !== JSON.stringify(other)) return false;
    }
    return true;
  }

  function saveState(currentNotations: Map<String, NotationAttributes>) {
    if (groupDepth > 0) {
      return;
    }

    undoStack.value.push(cloneDeep(currentNotations));
    if (undoStack.value.length > maxStackSize) {
      undoStack.value.shift(); // Remove oldest state
    }
    redoStack.value = []; // Clear redo stack when new action is performed
  }

  function beginBulkSave(currentNotations: Map<String, NotationAttributes>) {
    if (groupDepth === 0) {
      groupSnapshot = cloneDeep(currentNotations);
    }
    groupDepth += 1;
  }

  function endBulkSave(currentNotations: Map<String, NotationAttributes>) {
    if (groupDepth === 0) {
      return;
    }

    groupDepth -= 1;
    if (groupDepth > 0) {
      return;
    }

    if (groupSnapshot && !areMapsEqual(groupSnapshot, currentNotations)) {
      undoStack.value.push(groupSnapshot);
      if (undoStack.value.length > maxStackSize) {
        undoStack.value.shift();
      }
      redoStack.value = [];
    }
    groupSnapshot = null;
  }

  async function undo(
    currentNotations: Ref<Map<String, NotationAttributes>>,
    rebuildOccupationMatrices: () => void,
  ) {
    if (undoStack.value.length === 0) return;

    redoStack.value.push(cloneDeep(currentNotations.value));
    const restoredState = undoStack.value.pop()!;

    // Sync diff to DB
    await syncToDB(restoredState, currentNotations.value);

    currentNotations.value = cloneDeep(restoredState);
    rebuildOccupationMatrices();
  }

  async function redo(
    currentNotations: Ref<Map<String, NotationAttributes>>,
    rebuildOccupationMatrices: () => void,
  ) {
    if (redoStack.value.length === 0) return;

    const previousState = cloneDeep(currentNotations.value);
    undoStack.value.push(previousState);
    const nextState = redoStack.value.pop()!;
    currentNotations.value = cloneDeep(nextState);
    rebuildOccupationMatrices();

    // Sync diff to DB
    await syncToDB(currentNotations.value, previousState);
  }

  return {
    saveState,
    beginBulkSave,
    endBulkSave,
    undo,
    redo,
    canUndo: computed(() => undoStack.value.length > 0),
    canRedo: computed(() => redoStack.value.length > 0),
  };
}
